<template>
  <div :class="compact ? 'car-selector-compact' : 'car-selector-manager'">
    <!-- Компактный режим: главная страница (только селекты в строку) -->
    <div v-if="compact" class="mark_podbor">
      <v-select
        v-model="selectedCategory"
        :options="categories"
        label="NAME"
        :reduce="category => category"
        placeholder="Выберите категорию"
        class="selection-select-compact"
      >
        <template #option="option">
          <div class="select-option">
            <img v-if="option.PICTURE" :src="resolvePictureUrl(option.PICTURE)" alt="" class="select-option-img" @error="onImgError" />
            <span class="option-text">{{ getCategoryShortName(option.NAME) }}</span>
          </div>
        </template>
        <template #selected-option="option">
          <span>{{ getCategoryShortName(option.NAME) }}</span>
        </template>
      </v-select>

      <v-select
        ref="brandSelect"
        v-model="selectedBrand"
        :options="brands"
        label="NAME"
        v-if="brands.length"
        :reduce="brand => brand"
        placeholder="Выберите марку"
        class="selection-select-compact"
      >
        <template #option="option">
          <div class="select-option">
            <img v-if="option.PICTURE" :src="resolvePictureUrl(option.PICTURE)" alt="" class="select-option-img" @error="onImgError" />
            <span class="option-text">{{ getBrandDisplayName(option.NAME) }}</span>
          </div>
        </template>
        <template #selected-option="option">
          <span>{{ getBrandDisplayName(option.NAME) }}</span>
        </template>
      </v-select>

      <v-select
        ref="modelSelect"
        v-model="selectedModel"
        :options="models"
        label="NAME"
        v-if="models.length"
        :reduce="model => model"
        placeholder="Выберите модель"
        class="selection-select-compact"
      >
        <template #option="option">
          <div class="select-option">
            <img v-if="option.PICTURE" :src="resolvePictureUrl(option.PICTURE)" alt="" class="select-option-img" @error="onImgError" />
            <span class="option-text">{{ option.NAME }}</span>
          </div>
        </template>
      </v-select>

      <v-select
        ref="generationSelect"
        v-model="selectedGeneration"
        :options="generations"
        label="NAME"
        v-if="generations.length"
        :reduce="generation => generation"
        placeholder="Выберите поколение"
        class="selection-select-compact"
      >
        <template #option="option">
          <div class="select-option">
            <span class="option-text">{{ option.NAME }}</span>
          </div>
        </template>
      </v-select>

      <v-select
        ref="engineSelect"
        v-model="selectedEngine"
        :options="engines"
        label="NAME"
        v-if="engines.length"
        :reduce="engine => engine"
        placeholder="Выберите двигатель"
        class="selection-select-compact"
      >
        <template #option="option">
          <div class="select-option">
            <span class="option-text">{{ option.NAME }}</span>
          </div>
        </template>
      </v-select>

      <div v-if="selectedEngineUrl" class="compact-engine-link">
        <a :href="selectedEngineUrl" target="_blank" rel="noopener">Ссылка на подбор</a>
        <button type="button" class="compact-copy-btn" @click="copyToClipboard(selectedEngineUrl)" title="Копировать">⎘</button>
      </div>
    </div>

    <!-- Полный режим: модальное окно в заказе -->
    <template v-else>
    <div class="car-selector-panel">
    <!-- Категории транспорта -->
    <div class="main-category-buttons">
      <button
        v-for="category in mainCategories"
        :key="category.ID"
        type="button"
        @click="selectCategory(category)"
        :class="['main-category-btn', { active: selectedCategory && selectedCategory.ID === category.ID }]"
      >
        <div class="category-icon">
          <img v-if="category.PICTURE" :src="resolvePictureUrl(category.PICTURE)" :alt="category.NAME" @error="onImgError" />
          <span v-else class="category-icon-fallback">{{ getCategoryShortName(category.NAME).charAt(0) }}</span>
        </div>
        <span class="category-name">{{ getCategoryShortName(category.NAME) }}</span>
      </button>
    </div>

    <!-- Хлебные крошки + назад -->
    <div v-if="selectedCategory" class="toolbar-row">
      <div class="breadcrumb-trail">
        <button type="button" class="crumb" @click="resetToCategories">{{ getCategoryShortName(selectedCategory.NAME) }}</button>
        <template v-if="selectedBrand">
          <span class="crumb-sep">›</span>
          <button type="button" class="crumb" @click="goToBrands">{{ selectedBrand.NAME }}</button>
        </template>
        <template v-if="selectedModel">
          <span class="crumb-sep">›</span>
          <button type="button" class="crumb" @click="goToModels">{{ selectedModel.NAME }}</button>
        </template>
        <template v-if="selectedGeneration">
          <span class="crumb-sep">›</span>
          <span class="crumb crumb_current">{{ selectedGeneration.NAME }}</span>
        </template>
      </div>
      <button v-if="canGoBack" type="button" class="back-button" @click="goBack">← Назад</button>
    </div>

    <!-- Алфавитный фильтр (марки) -->
    <div v-if="selectedCategory && gridLevel === 'brands' && brands.length" class="alphabet-filter">
      <div class="alphabet-list">
        <span
          :class="['alphabet-letter', { active: !selectedLetter }]"
          @click="filterByLetter(null)"
        >Все</span>
        <span
          v-for="letter in (isRussianAlphabet ? russianAlphabet : latinAlphabet)"
          :key="letter"
          @click="filterByLetter(letter)"
          :class="['alphabet-letter', { active: selectedLetter === letter, disabled: !hasBrandsForLetter(letter) }]"
        >{{ letter }}</span>
        <span class="separator">|</span>
        <span
          @click="toggleAlphabet"
          :class="['alphabet-letter', { active: isRussianAlphabet }]"
        >{{ isRussianAlphabet ? 'LAT' : 'RUS' }}</span>
      </div>
    </div>

    <!-- Быстрый поиск через селекты -->
    <div v-if="selectedCategory" class="selection-dropdowns">
      <v-select
        ref="brandSelect"
        v-model="selectedBrand"
        :options="brandSelectOptions"
        label="NAME"
        :reduce="brand => brand"
        placeholder="Выберите марку"
        class="selection-select"
      >
        <template #option="option">
          <div class="select-option">
            <img v-if="option.PICTURE" :src="resolvePictureUrl(option.PICTURE)" alt="" class="select-option-img" @error="onImgError" />
            <span class="option-text">{{ getBrandDisplayName(option.NAME) }}</span>
          </div>
        </template>
        <template #selected-option="option">
          <span>{{ getBrandDisplayName(option.NAME) }}</span>
        </template>
      </v-select>

      <v-select
        ref="modelSelect"
        v-model="selectedModel"
        :options="models"
        label="NAME"
        v-if="models.length"
        :reduce="model => model"
        placeholder="Выберите модель"
        class="selection-select"
      >
        <template #option="option">
          <div class="select-option">
            <img v-if="option.PICTURE" :src="resolvePictureUrl(option.PICTURE)" alt="" class="select-option-img" @error="onImgError" />
            <span class="option-text">{{ option.NAME }}</span>
          </div>
        </template>
      </v-select>

      <v-select
        ref="generationSelect"
        v-model="selectedGeneration"
        :options="generations"
        label="NAME"
        v-if="generations.length"
        :reduce="generation => generation"
        placeholder="Выберите поколение"
        class="selection-select"
      >
        <template #option="option">
          <div class="select-option">
            <span class="option-text">{{ option.NAME }}</span>
          </div>
        </template>
      </v-select>

      <v-select
        ref="engineSelect"
        v-model="selectedEngine"
        :options="engines"
        label="NAME"
        v-if="engines.length"
        :reduce="engine => engine"
        placeholder="Выберите двигатель"
        class="selection-select"
      >
        <template #option="option">
          <div class="select-option">
            <span class="option-text">{{ option.NAME }}</span>
          </div>
        </template>
      </v-select>
    </div>

    <!-- Сетка выбора -->
    <div v-if="selectedCategory" class="brands-grid-section">
      <div class="grid-header">
        <h3 class="grid-title">{{ currentGridTitle }}</h3>
        <span v-if="!isLoading && currentGridItems.length" class="grid-count">{{ currentGridItems.length }} поз.</span>
      </div>

      <div v-if="!isLoading && !currentGridItems.length" class="grid-empty">
        Ничего не найдено. Попробуйте другую букву или вернитесь назад.
      </div>

      <div class="brands-grid" ref="brandsGridRef">
        <template v-if="isLoading">
          <div
            v-for="i in 24"
            :key="'sk-' + i"
            class="brand-item skeleton-item"
          >
            <div class="brand-logo skeleton-logo"></div>
            <div class="brand-name skeleton-text"></div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="(item, idx) in currentGridItems"
            :key="gridItemKey(item, idx)"
            @click="selectGridItem(item)"
            :class="['brand-item', { active: isItemSelected(item) }]"
          >
            <div class="brand-logo">
              <img :src="getItemImage(item)" :alt="item.NAME" @error="onImgError" />
            </div>
            <div class="brand-name">{{ getItemDisplayName(item) }}</div>
          </div>
        </template>
      </div>
    </div>

    <div v-else class="pick-category-hint">
      Выберите тип транспорта, чтобы открыть список марок
    </div>
    </div>
    </template>

    <!-- Панель выбранного двигателя (только полный режим) -->
    <div v-if="!compact && selectedEngine && selectedEngineUrl" class="selection-footer">
      <div class="selection-footer-info">
        <div class="selection-footer-label">Выбрано</div>
        <div class="selection-footer-path">{{ selectionPathLabel }}</div>
        <a :href="selectedEngineUrl" target="_blank" rel="noopener" class="selection-footer-link">{{ selectedEngineUrl }}</a>
      </div>
      <div class="selection-footer-actions">
        <button type="button" class="btn-copy" @click="copyToClipboard(selectedEngineUrl)">Копировать ссылку</button>
        <button type="button" class="btn-apply" @click="applySelection">Применить к заказу</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

const API_BASE = 'https://1akb.by/api.php';

export default {
  name: 'CarSelector',
  components: { 'v-select': vSelect },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      categories: [],
      brands: [],
      models: [],
      generations: [],
      engines: [],
      selectedCategory: null,
      selectedBrand: null,
      selectedModel: null,
      selectedGeneration: null,
      selectedEngine: null,
      selectedEngineUrl: '',
      selectedLetter: null,
      isRussianAlphabet: false,
      isLoading: false,
      gridUpdateTrigger: 0,
      latinAlphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
      russianAlphabet: 'АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЭЮЯ'.split(''),
      imgFallback: 'https://1akb.by/images/benzin.png',
    };
  },
  computed: {
    mainCategories() {
      return this.categories || [];
    },
    filteredBrands() {
      if (!this.brands.length) return [];
      if (!this.selectedLetter) return this.brands;
      return this.brands.filter(b => String(b.NAME || '').toUpperCase().startsWith(this.selectedLetter));
    },
    brandSelectOptions() {
      return this.filteredBrands.map(b => ({ ...b }));
    },
    gridLevel() {
      if (this.selectedGeneration && this.engines.length) return 'engines';
      if (this.selectedModel && this.generations.length) return 'generations';
      if (this.selectedModel && this.engines.length) return 'engines';
      if (this.selectedBrand && this.models.length) return 'models';
      if (this.selectedBrand && this.engines.length) return 'engines';
      return 'brands';
    },
    currentGridItems() {
      if (this.gridLevel === 'engines') return this.engines;
      if (this.gridLevel === 'generations') return this.generations;
      if (this.gridLevel === 'models') return this.models;
      return this.filteredBrands;
    },
    currentGridTitle() {
      if (this.selectedBrand && this.models.length) return `Модели ${this.selectedBrand.NAME}`;
      if (this.selectedModel && this.generations.length) return `Поколения ${this.selectedModel.NAME}`;
      if (this.selectedGeneration && this.engines.length) return `Двигатели ${this.selectedGeneration.NAME}`;
      if (this.selectedBrand && this.engines.length) return `Двигатели ${this.selectedBrand.NAME}`;
      if (this.selectedModel && this.engines.length) return `Двигатели ${this.selectedModel.NAME}`;
      return 'Марки транспорта';
    },
    canGoBack() {
      return !!(this.selectedBrand || this.selectedModel || this.selectedGeneration);
    },
    selectionPathLabel() {
      const b = this.selectedBrand;
      const m = this.selectedModel;
      const g = this.selectedGeneration;
      const e = this.selectedEngine;
      return [
        b && b.NAME,
        m && m.NAME,
        g && g.NAME,
        e && e.NAME,
      ].filter(Boolean).join(' · ');
    },
  },
  created() {
    this.getTransportCategories();
  },
  methods: {
    apiGet(action, params = {}) {
      const q = new URLSearchParams({ action, ...params });
      return axios.get(`${API_BASE}?${q.toString()}`);
    },
    resolvePictureUrl(picture) {
      if (!picture) return '';
      const src = String(picture);
      if (src.indexOf('http://') === 0 || src.indexOf('https://') === 0) return src;
      if (src.charAt(0) === '/') return 'https://1akb.by' + src;
      return src;
    },
    parseApiList(data) {
      if (!data) return [];
      const list = Array.isArray(data) ? data : Object.values(data);
      return list
        .map(raw => this.normalizeListItem(raw))
        .filter(item => item && item.ID);
    },
    normalizeListItem(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const name = raw.NAME || raw.name || '';
      const item = {
        ID: String(raw.ID != null ? raw.ID : (raw.id != null ? raw.id : '')),
        NAME: String(name),
        PICTURE: this.resolvePictureUrl(raw.PICTURE || raw.picture),
        SORT: raw.SORT || raw.sort,
      };
      if (raw.DETAIL_PAGE_URL || raw.detail_page_url) {
        item.DETAIL_PAGE_URL = raw.DETAIL_PAGE_URL || raw.detail_page_url;
      }
      if (raw.CORPUS || raw.corpus) {
        item.CORPUS = raw.CORPUS || raw.corpus;
      }
      return item;
    },
    getCategoryShortName(name) {
      const map = {
        'для Легковых автомобилей': 'Легковые',
        'для Мотоциклов и скутеров': 'Мотоциклы',
        'для Грузовых автомобилей': 'Грузовые',
        'для Источников бесперебойного питания (ИБП)': 'ИБП',
        'для Квадроциклов': 'Квадроциклы',
        'для Снегоходов': 'Снегоходы',
        'для Лодочных моторов': 'Лодочные моторы',
      };
      return map[name] || name;
    },
    getBrandDisplayName(name) {
      if (!name) return '';
      const t = {
        'Alfa Romeo': 'Alfa Romeo (Альфа Ромео)',
        Audi: 'Audi (Ауди)',
        BMW: 'BMW (БМВ)',
        Chevrolet: 'Chevrolet (Шевроле)',
        Citroen: 'Citroen (Ситроен)',
        Ford: 'Ford (Форд)',
        Honda: 'Honda (Хонда)',
        Hyundai: 'Hyundai (Хёндай)',
        Kia: 'Kia (Киа)',
        Lada: 'Lada (Лада)',
        'Mercedes-Benz': 'Mercedes-Benz (Мерседес)',
        Nissan: 'Nissan (Ниссан)',
        Opel: 'Opel (Опель)',
        Peugeot: 'Peugeot (Пежо)',
        Renault: 'Renault (Рено)',
        Toyota: 'Toyota (Тойота)',
        Volkswagen: 'Volkswagen (Фольксваген)',
        Volvo: 'Volvo (Вольво)',
        UAZ: 'UAZ (УАЗ)',
        Geely: 'Geely (Джили)',
      };
      return t[name] || name;
    },
    getItemDisplayName(item) {
      if (!item || typeof item !== 'object') return '';
      const name = item.NAME || item.name || '';
      if (!name) return '';
      if (this.gridLevel === 'brands') {
        return this.getBrandDisplayName(name) || name;
      }
      return name;
    },
    getItemImage(item) {
      if (!item) return this.imgFallback;
      if (this.gridLevel === 'engines') {
        const n = String(item.NAME || '').toLowerCase();
        if (n.includes('дизель') || n.includes('diesel')) return 'https://1akb.by/images/disel.png';
        if (n.includes('бензин') || n.includes('petrol') || n.includes('gasoline')) return 'https://1akb.by/images/benzin.png';
      }
      return this.resolvePictureUrl(item.PICTURE) || this.imgFallback;
    },
    onImgError(e) {
      e.target.src = this.imgFallback;
    },
    gridItemKey(item, idx) {
      const id = item && item.ID != null ? item.ID : 'item';
      return `${id}-${this.gridUpdateTrigger}-${idx}`;
    },
    hasBrandsForLetter(letter) {
      return this.brands.some(b => String(b.NAME || '').toUpperCase().startsWith(letter));
    },
    filterByLetter(letter) {
      this.selectedLetter = letter;
    },
    toggleAlphabet() {
      this.isRussianAlphabet = !this.isRussianAlphabet;
      this.selectedLetter = null;
    },
    async getTransportCategories() {
      try {
        const { data } = await this.apiGet('getTransportCategories');
        this.categories = this.parseApiList(data);
        if (!this.compact) {
          const passenger = this.categories.find(c => c.NAME && /легковых/i.test(c.NAME));
          if (passenger) this.selectCategory(passenger);
        }
      } catch (e) {
        console.error(e);
      }
    },
    selectCategory(category) {
      this.selectedCategory = category;
      this.selectedLetter = null;
      this.clearSelections('brand');
      this.getCarBrands();
    },
    resetToCategories() {
      this.selectedCategory = null;
      this.clearSelections('brand');
      this.brands = [];
      this.models = [];
      this.generations = [];
      this.engines = [];
    },
    goToBrands() {
      this.selectedBrand = null;
      this.clearSelectedOnly('brand');
      this.getCarBrands();
    },
    goToModels() {
      this.selectedModel = null;
      this.clearSelectedOnly('model');
      this.getCarModels();
    },
    async getCarBrands() {
      if (!this.selectedCategory) return;
      try {
        if (!this.compact) this.isLoading = true;
        this.clearSelections('brand');
        const { data } = await this.apiGet('getCarBrands', { categoryId: this.selectedCategory.ID });
        this.brands = this.parseApiList(data);
        this.gridUpdateTrigger++;
      } catch (e) {
        console.error(e);
      } finally {
        if (!this.compact) {
          setTimeout(() => { this.isLoading = false; }, 200);
        }
      }
    },
    async getCarModels() {
      if (!this.selectedBrand) return;
      try {
        if (!this.compact) this.isLoading = true;
        this.clearSelections('model');
        const { data } = await this.apiGet('getCarModels', { brandId: this.selectedBrand.ID });
        if (data && data.length) {
          this.models = this.parseApiList(data);
          this.gridUpdateTrigger++;
        } else {
          await this.getCarEngines('brand');
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!this.compact) {
          setTimeout(() => { this.isLoading = false; }, 200);
        }
      }
    },
    async getCarGenerations() {
      if (!this.selectedModel) return;
      try {
        if (!this.compact) this.isLoading = true;
        this.clearSelections('generation');
        const { data } = await this.apiGet('getCarGenerations', { modelId: this.selectedModel.ID });
        if (data && data.length) {
          this.generations = this.parseApiList(data);
          this.gridUpdateTrigger++;
        } else {
          await this.getCarEngines('model');
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!this.compact) {
          setTimeout(() => { this.isLoading = false; }, 200);
        }
      }
    },
    async getCarEngines(level) {
      let parentId = null;
      if (level === 'generation') parentId = this.selectedGeneration && this.selectedGeneration.ID;
      else if (level === 'model') parentId = this.selectedModel && this.selectedModel.ID;
      else if (level === 'brand') parentId = this.selectedBrand && this.selectedBrand.ID;
      if (!parentId) return;
      try {
        if (!this.compact) this.isLoading = true;
        this.clearSelections('engine');
        const { data } = await this.apiGet('getCarEngines', { parentId });
        let engines = this.parseApiList(data);
        if (!this.$root.corpus) this.$root.corpus = [];
        this.$root.corpus = [];
        engines.forEach(engine => {
          if (engine && engine.CORPUS && Array.isArray(engine.CORPUS)) {
            engine.CORPUS.forEach(c => {
              if (c && !this.$root.corpus.includes(c)) this.$root.corpus.push(c);
            });
          }
        });
        if (this.$root.corpus.length) {
          engines = engines.filter(engine =>
            engine.CORPUS && engine.CORPUS.some(c => this.$root.corpus.includes(c))
          );
        }
        this.engines = engines;
        this.gridUpdateTrigger++;
      } catch (e) {
        console.error(e);
      } finally {
        if (!this.compact) {
          setTimeout(() => { this.isLoading = false; }, 200);
        }
      }
    },
    selectGridItem(item) {
      if (this.gridLevel === 'brands') {
        this._skipBrandWatch = true;
        this.selectedBrand = item;
        this.$nextTick(() => { this._skipBrandWatch = false; });
        this.clearSelections('model');
        this.getCarModels();
        return;
      }
      if (this.gridLevel === 'models') {
        this.selectedModel = item;
        this.clearSelections('generation');
        this.getCarGenerations();
        return;
      }
      if (this.gridLevel === 'generations') {
        this.selectedGeneration = item;
        this.clearSelections('engine');
        this.getCarEngines('generation');
        return;
      }
      if (this.gridLevel === 'engines') {
        this.selectedEngine = item;
        this.updateSelectedEngineUrl();
        this.applySelection();
      }
    },
    isItemSelected(item) {
      if (this.gridLevel === 'brands') return this.selectedBrand && this.selectedBrand.ID === item.ID;
      if (this.gridLevel === 'models') return this.selectedModel && this.selectedModel.ID === item.ID;
      if (this.gridLevel === 'generations') return this.selectedGeneration && this.selectedGeneration.ID === item.ID;
      if (this.gridLevel === 'engines') return this.selectedEngine && this.selectedEngine.ID === item.ID;
      return false;
    },
    goBack() {
      if (this.engines.length && this.selectedGeneration) {
        this.selectedGeneration = null;
        this.clearSelectedOnly('generation');
        this.getCarGenerations();
      } else if (this.engines.length && this.selectedModel && !this.selectedGeneration) {
        this.selectedModel = null;
        this.clearSelectedOnly('model');
        this.getCarModels();
      } else if (this.engines.length && this.selectedBrand && !this.selectedModel) {
        this.selectedBrand = null;
        this.clearSelectedOnly('brand');
        this.getCarBrands();
      } else if (this.generations.length) {
        this.selectedModel = null;
        this.clearSelectedOnly('model');
        this.getCarModels();
      } else if (this.models.length) {
        this.selectedBrand = null;
        this.clearSelectedOnly('brand');
        this.getCarBrands();
      }
    },
    clearSelections(level) {
      if (level === 'brand') {
        this.selectedBrand = null;
        this.brands = [];
        this.selectedLetter = null;
        this.clearSelections('model');
      }
      if (level === 'model') {
        this.selectedModel = null;
        this.models = [];
        this.clearSelections('generation');
      }
      if (level === 'generation') {
        this.selectedGeneration = null;
        this.generations = [];
        this.clearSelections('engine');
      }
      if (level === 'engine') {
        this.selectedEngine = null;
        this.engines = [];
        this.selectedEngineUrl = '';
      }
    },
    clearSelectedOnly(level) {
      if (level === 'brand') {
        this.selectedBrand = null;
        this.clearSelectedOnly('model');
      }
      if (level === 'model') {
        this.selectedModel = null;
        this.clearSelectedOnly('generation');
      }
      if (level === 'generation') {
        this.selectedGeneration = null;
        this.clearSelectedOnly('engine');
      }
      if (level === 'engine') {
        this.selectedEngine = null;
        this.selectedEngineUrl = '';
      }
    },
    updateSelectedEngineUrl() {
      if (!this.selectedEngine) {
        this.selectedEngineUrl = '';
        if (this.compact) {
          this.$emit('update-link', '');
          this.$emit('update-corpus', []);
        }
        return;
      }
      this.selectedEngineUrl = this.selectedEngine.DETAIL_PAGE_URL || '';
      if (this.compact) {
        const corpusArray = Array.isArray(this.selectedEngine.CORPUS)
          ? [...this.selectedEngine.CORPUS].filter(Boolean)
          : (this.selectedEngine.CORPUS ? [this.selectedEngine.CORPUS] : []);
        this.$emit('update-link', this.selectedEngineUrl);
        this.$emit('update-corpus', corpusArray);
      }
    },
    extractYear(name) {
      if (!name) return '';
      const m = String(name).match(/(\d{4})\s*[-–]\s*(\d{4})/);
      if (m) return m[1];
      const y = String(name).match(/(\d{4})/);
      return y ? y[1] : '';
    },
    buildSelectionPayload() {
      const engine = this.selectedEngine;
      if (!engine) return null;
      const fuelMatch = engine.NAME && String(engine.NAME).match(/\(([^)]+)\)\s*$/);
      const fuelType = fuelMatch ? fuelMatch[1] : '';
      const corpusArray = Array.isArray(engine.CORPUS) ? [...engine.CORPUS].filter(Boolean) : [];
      const carBrand = (this.selectedBrand && this.selectedBrand.NAME) || '';
      const carModel = (this.selectedModel && this.selectedModel.NAME) || '';
      const genName = this.selectedGeneration && this.selectedGeneration.NAME;
      const carName = [genName, engine.NAME].filter(Boolean).join(' — ') || carModel;
      const carYear = this.extractYear(genName);
      const carBodyType = corpusArray.join(', ');
      const auto = [carBrand, carModel, genName, fuelType, carBodyType].filter(Boolean).join(' ');
      return {
        url: engine.DETAIL_PAGE_URL || '',
        corpus: corpusArray,
        carBrand,
        carModel,
        carName,
        carYear,
        fuelType,
        carBodyType,
        auto,
      };
    },
    applySelection() {
      const payload = this.buildSelectionPayload();
      if (!payload || !payload.url) return;
      this.$emit('select', payload);
      this.$emit('update-link', payload.url);
      this.$emit('update-corpus', payload.corpus);
    },
    copyToClipboard(url) {
      navigator.clipboard.writeText(url).then(() => {
        alert('Ссылка скопирована');
      }).catch(() => {});
    },
  },
  watch: {
    selectedCategory(val) {
      if (!this.compact) return;
      this.clearSelections('brand');
      if (val) this.getCarBrands();
    },
    selectedBrand(val) {
      if (this._skipBrandWatch || !val) return;
      this.clearSelections('model');
      this.getCarModels();
    },
    selectedModel(val) {
      if (val) {
        this.clearSelections('generation');
        this.getCarGenerations();
      }
    },
    selectedGeneration(val) {
      if (val) {
        this.clearSelections('engine');
        this.getCarEngines('generation');
      }
    },
    selectedEngine() {
      this.updateSelectedEngineUrl();
    },
  },
};
</script>

<style scoped>
.car-selector-manager {
  --cs-primary: #005eeb;
  --cs-primary-hover: #0049c2;
  --cs-primary-soft: #e8f1ff;
  --cs-surface: #ffffff;
  --cs-surface-muted: #f8fafc;
  --cs-bg: #e8edf4;
  --cs-border: #e2e8f0;
  --cs-text: #0f172a;
  --cs-muted: #64748b;
  --cs-radius: 10px;
  --cs-radius-sm: 6px;
  --cs-pad: 16px 20px;
  --cs-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 12px 32px rgba(15, 23, 42, 0.08);

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 20px 24px;
  gap: 14px;
  background: var(--cs-bg);
  box-sizing: border-box;
}

.car-selector-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--cs-surface);
  border: 1px solid var(--cs-border);
  border-radius: 12px;
  box-shadow: var(--cs-shadow);
  overflow: hidden;
}

.main-category-buttons {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: var(--cs-surface-muted);
  border-bottom: 1px solid var(--cs-border);
  flex-shrink: 0;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.main-category-buttons::-webkit-scrollbar {
  height: 4px;
}

.main-category-buttons::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.main-category-btn {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 6px;
  border: 1px solid var(--cs-border);
  border-radius: var(--cs-radius-sm);
  background: var(--cs-surface);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  min-height: 0;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.main-category-btn:hover {
  border-color: var(--cs-primary);
  background: var(--cs-primary-soft);
}

.main-category-btn.active {
  border-color: var(--cs-primary);
  background: var(--cs-primary);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 94, 235, 0.28);
}

.category-icon {
  width: 22px;
  height: 22px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.category-icon img {
  max-height: 22px;
  max-width: 22px;
  object-fit: contain;
}

.category-icon-fallback {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: rgba(0, 94, 235, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  color: var(--cs-primary);
}

.main-category-btn.active .category-icon-fallback {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.category-name {
  font-size: 11px;
  font-weight: 600;
  text-align: left;
  line-height: 1.2;
  white-space: nowrap;
}

.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: var(--cs-pad);
  padding-top: 12px;
  padding-bottom: 12px;
  background: var(--cs-surface);
  border-bottom: 1px solid var(--cs-border);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.breadcrumb-trail {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  font-size: 13px;
}

.crumb {
  background: none;
  border: none;
  color: var(--cs-primary);
  cursor: pointer;
  padding: 2px 4px;
  font-size: 13px;
}

.crumb:hover {
  text-decoration: underline;
}

.crumb_current {
  color: var(--cs-text);
  font-weight: 600;
}

.crumb-sep {
  color: #94a3b8;
}

.back-button {
  background: var(--cs-surface);
  border: 1px solid var(--cs-border);
  border-radius: var(--cs-radius-sm);
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--cs-text);
  white-space: nowrap;
}

.back-button:hover {
  background: var(--cs-primary);
  border-color: var(--cs-primary);
  color: #fff;
}

.alphabet-filter {
  padding: 10px 20px;
  background: var(--cs-surface-muted);
  border-bottom: 1px solid var(--cs-border);
  flex-shrink: 0;
}

.alphabet-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.alphabet-letter {
  padding: 3px 7px;
  border: 1px solid var(--cs-border);
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  background: var(--cs-surface);
  color: var(--cs-muted);
  user-select: none;
}

.alphabet-letter:hover:not(.disabled) {
  border-color: var(--cs-primary);
  color: var(--cs-primary);
  background: var(--cs-primary-soft);
}

.alphabet-letter.active {
  background: var(--cs-primary);
  color: #fff;
  border-color: var(--cs-primary);
}

.alphabet-letter.disabled {
  opacity: 0.35;
  cursor: default;
}

.separator {
  color: #9ca3af;
  margin: 0 4px;
}

.selection-dropdowns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  padding: var(--cs-pad);
  padding-top: 12px;
  padding-bottom: 12px;
  background: var(--cs-surface);
  border-bottom: 1px solid var(--cs-border);
  flex-shrink: 0;
}

.brands-grid-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: var(--cs-pad);
  padding-top: 14px;
  overflow: hidden;
  background: var(--cs-surface);
}

.grid-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-shrink: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--cs-border);
}

.grid-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--cs-text);
  letter-spacing: -0.02em;
}

.grid-count {
  font-size: 12px;
  font-weight: 500;
  color: var(--cs-muted);
  background: var(--cs-surface-muted);
  border: 1px solid var(--cs-border);
  border-radius: 20px;
  padding: 4px 10px;
}

.grid-empty {
  padding: 48px 24px;
  text-align: center;
  color: var(--cs-muted);
  font-size: 14px;
  background: var(--cs-surface-muted);
  border-radius: var(--cs-radius);
  border: 1px dashed var(--cs-border);
}

.brands-grid {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
  gap: 10px;
  align-content: start;
  padding: 4px 2px 8px 0;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.brand-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border: 1px solid var(--cs-border);
  border-radius: var(--cs-radius);
  background: var(--cs-surface);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  min-height: 92px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.brand-item:hover {
  border-color: var(--cs-primary);
  background: var(--cs-primary-soft);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 94, 235, 0.14);
}

.brand-item.active {
  border-color: var(--cs-primary);
  background: var(--cs-primary-soft);
  box-shadow: 0 0 0 2px rgba(0, 94, 235, 0.18);
}

.brand-logo {
  height: 44px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-logo img {
  max-width: 100%;
  max-height: 44px;
  object-fit: contain;
}

.brand-name {
  font-size: 11px;
  font-weight: 600;
  color: var(--cs-text);
  text-align: center;
  line-height: 1.25;
  word-break: break-word;
}

.skeleton-item {
  pointer-events: none;
}

.skeleton-logo,
.skeleton-text {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite;
  border-radius: 4px;
}

.skeleton-logo {
  width: 48px;
  height: 48px;
  margin-bottom: 8px;
}

.skeleton-text {
  width: 70%;
  height: 10px;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.pick-category-hint {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cs-muted);
  font-size: 15px;
  padding: 48px 24px;
  background: var(--cs-surface-muted);
}

.selection-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  margin: 0;
  background: var(--cs-surface);
  border: 1px solid var(--cs-border);
  border-radius: 12px;
  box-shadow: var(--cs-shadow);
}

.selection-footer-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.selection-footer-path {
  font-size: 14px;
  font-weight: 600;
  color: var(--cs-text);
  margin: 4px 0;
}

.selection-footer-link {
  font-size: 12px;
  color: var(--cs-primary);
  word-break: break-all;
}

.selection-footer-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-copy,
.btn-apply {
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-copy {
  background: var(--cs-surface-muted);
  color: var(--cs-text);
  border: 1px solid var(--cs-border);
}

.btn-copy:hover {
  border-color: var(--cs-primary);
  color: var(--cs-primary);
}

.btn-apply {
  background: var(--cs-primary);
  color: #fff;
}

.btn-apply:hover {
  background: var(--cs-primary-hover);
}

.select-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-option-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}
</style>

<style>
/* vue-select внутри подбора */
.car-selector-manager .selection-select .vs__dropdown-toggle {
  min-height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 10px;
  background: #fff;
  font-size: 13px;
}

.car-selector-manager .selection-select .vs__dropdown-toggle:focus-within {
  border-color: #005eeb;
  box-shadow: 0 0 0 3px rgba(0, 94, 235, 0.1);
}

.car-selector-manager .vs__dropdown-menu {
  z-index: 10050 !important;
}

/* Компактный подбор на главной (manager.1akb.by) */
.car-selector-compact {
  width: 100%;
}

.car-selector-compact .mark_podbor {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  width: 100%;
}

.car-selector-compact .selection-select-compact {
  flex: 0 1 auto;
  min-width: 140px;
  max-width: 220px;
  margin-right: 2px;
  margin-bottom: 2px;
}

.car-selector-compact .vs__dropdown-toggle {
  min-width: 140px;
  min-height: 34px;
  border-color: #dee2e6;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
}

.car-selector-compact .select-option-img {
  width: 20px;
  height: 20px;
}

.car-selector-compact .compact-engine-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  font-size: 12px;
}

.car-selector-compact .compact-engine-link a {
  color: #005eeb;
  text-decoration: none;
}

.car-selector-compact .compact-copy-btn {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #636f7f;
}

.car-selector-compact .vs__dropdown-menu {
  z-index: 1000 !important;
}
</style>
